/**
 * Auth Utilities - Funções de autenticação e logout
 */

import { signOut } from "next-auth/react";

/**
 * Limpa TODOS os dados armazenados localmente
 * - localStorage
 * - sessionStorage
 * - cookies (acessíveis via JS)
 * - caches do browser
 */
function clearAllStoredData(): void {
  if (typeof window === "undefined") return;

  // 1. Limpa TODO o localStorage
  try {
    localStorage.clear();
    console.log("[Auth] localStorage limpo");
  } catch (e) {
    console.error("[Auth] Erro ao limpar localStorage:", e);
  }

  // 2. Limpa TODO o sessionStorage
  try {
    sessionStorage.clear();
    console.log("[Auth] sessionStorage limpo");
  } catch (e) {
    console.error("[Auth] Erro ao limpar sessionStorage:", e);
  }

  // 3. Limpa cookies acessíveis via JavaScript
  try {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      if (name) {
        // Remove para o domínio atual e subdomínios
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        // Para domínios com prefixo (ex: .topamais.com.br)
        const domainParts = window.location.hostname.split(".");
        if (domainParts.length > 1) {
          const rootDomain = domainParts.slice(-2).join(".");
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${rootDomain}`;
        }
      }
    }
    console.log("[Auth] Cookies limpos");
  } catch (e) {
    console.error("[Auth] Erro ao limpar cookies:", e);
  }

  // 4. Limpa caches do browser (Service Worker caches)
  try {
    if ("caches" in window) {
      caches.keys().then((names) => {
        for (const name of names) {
          caches.delete(name);
        }
      });
      console.log("[Auth] Caches do browser limpos");
    }
  } catch (e) {
    console.error("[Auth] Erro ao limpar caches:", e);
  }
}

/**
 * Executa logout completo:
 * 1. Limpa todos os dados locais
 * 2. Chama signOut do NextAuth
 * 3. Redireciona para login
 */
export async function fullLogout(redirectTo = "/login"): Promise<void> {
  // Primeiro limpa tudo localmente
  clearAllStoredData();

  // Depois faz signOut do NextAuth (que limpa cookies httpOnly)
  await signOut({
    callbackUrl: redirectTo,
    redirect: true
  });
}

/**
 * Verifica se o erro é de autenticação (401)
 * e executa logout automático se necessário
 */
export function handleUnauthorizedError(status: number): boolean {
  if (status === 401) {
    console.warn("[Auth] Token expirado ou inválido. Executando logout automático...");
    // Executa logout em background (não bloqueia)
    fullLogout().catch(console.error);
    return true;
  }
  return false;
}

/**
 * Flag para evitar múltiplos logouts simultâneos
 */
let isLoggingOut = false;

/**
 * Executa logout automático (thread-safe)
 * Evita múltiplas chamadas simultâneas
 */
export async function autoLogoutOn401(): Promise<void> {
  if (isLoggingOut) {
    console.log("[Auth] Logout já em andamento, ignorando...");
    return;
  }

  isLoggingOut = true;
  try {
    await fullLogout();
  } finally {
    isLoggingOut = false;
  }
}
